import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import * as _ from 'lodash';
import { BehaviorSubject, concatMap, forkJoin, tap } from 'rxjs';

import { CuttingCustomHole, FastenerBasePointEnum } from '../../../../../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../../../../../models/cutting/cutting-fixed-fasteners-set.model';
import { CuttingPanelOutline, PlateTypeEnum } from '../../../../../models/cutting/cutting-panel-outline.model';
import { CuttingPanel } from '../../../../../models/cutting/cutting-panel.model';
import { Position } from '../../../../../models/cutting/cutting-svg.model';
import { FastenersBySides } from '../../panel-outline-editor-dialog.component';
import { FastenerBasePointHelper } from '../../../../../helpers/fastener-base-point.helper';
import {
  FastenersWizardDialogComponent,
  FastenersWizardDialogData,
} from '../../../../fasteners-wizard-dialog/fasteners-wizard-dialog.component';
import { CuttingPanelListService } from '../../../cutting-panel-list.service';
import { CuttingPanelBaseSlot } from '../../../../../models/cutting/cutting-panel-base-slot.model';
import { FastenersWizardDialogResult } from '../../../../fasteners-wizard-dialog/fasteners-wizard-dialog.types';

export interface CustomHolesByType {
  face: (CuttingCustomHole | CuttingFixedFastenersSet)[];
  back: (CuttingCustomHole | CuttingFixedFastenersSet)[];
  edge: (CuttingCustomHole | CuttingFixedFastenersSet)[];
}

export interface CustomHoleWithSide extends CuttingCustomHole {
  side: Position;
}

export interface FixedFastenersSetWithSide extends CuttingFixedFastenersSet {
  side: Position;
}

@Component({
  selector: 'csl-custom-fasteners',
  templateUrl: './custom-fasteners.component.html',
  styleUrls: ['./custom-fasteners.component.scss'],
})
export class CustomFastenersComponent {
  commonHoleList: CustomHoleWithSide[] = [];
  commonSetsList: FixedFastenersSetWithSide[] = [];

  customHoleWizardDialogRef: MatDialogRef<FastenersWizardDialogComponent>;
  @Output() selectedHoleChange: BehaviorSubject<CuttingCustomHole[] | undefined> = new BehaviorSubject(undefined);
  @Output() selectedFastenersSetChange: BehaviorSubject<CuttingFixedFastenersSet | undefined> = new BehaviorSubject(
    undefined
  );

  @Input() editable = true;
  @Input() panel: CuttingPanel;
  @Input() selectedFastenersSet: CuttingFixedFastenersSet | undefined;
  @Input() selectedHole: CuttingCustomHole[] | undefined;

  @Input() mainMaterialThickness = 0;

  @Output() formChanged: EventEmitter<FastenersBySides> = new EventEmitter();

  get outline(): CuttingPanelOutline<CuttingPanelBaseSlot> {
    return this.panel.panelOutline;
  }

  get groupedSets(): CustomHolesByType {
    this.commonSetsList = [
      ...(this.panel.panelOutline.topSide.fastenersSets as FixedFastenersSetWithSide[]).map(s => {
        s.side = 'tc';
        return s;
      }),
      ...(this.panel.panelOutline.rightSide.fastenersSets as FixedFastenersSetWithSide[]).map(s => {
        s.side = 'rc';
        return s;
      }),
      ...(this.panel.panelOutline.bottomSide.fastenersSets as FixedFastenersSetWithSide[]).map(s => {
        s.side = 'bc';
        return s;
      }),
      ...(this.panel.panelOutline.leftSide.fastenersSets as FixedFastenersSetWithSide[]).map(s => {
        s.side = 'lc';
        return s;
      }),
    ].sort((a, b) => (a.name > b.name ? 1 : -1));

    const grouped: CustomHolesByType = { face: [], back: [], edge: [] };
    this.commonSetsList.forEach(s => {
      switch (s.plate) {
        case PlateTypeEnum.Face:
          grouped.face.push(s);
          break;

        case PlateTypeEnum.Back:
          grouped.back.push(s);
          break;

        case PlateTypeEnum.Edge:
          grouped.edge.push(s);
          break;
      }
    });

    return grouped;
  }

  get groupedHoles(): CustomHolesByType {
    this.commonHoleList = [
      ...(this.panel.panelOutline.topSide.customHoles as CustomHoleWithSide[]).map(s => {
        s.side = 'tc';
        return s;
      }),
      ...(this.panel.panelOutline.rightSide.customHoles as CustomHoleWithSide[]).map(s => {
        s.side = 'rc';
        return s;
      }),
      ...(this.panel.panelOutline.bottomSide.customHoles as CustomHoleWithSide[]).map(s => {
        s.side = 'bc';
        return s;
      }),
      ...(this.panel.panelOutline.leftSide.customHoles as CustomHoleWithSide[]).map(s => {
        s.side = 'lc';
        return s;
      }),
    ].sort((a, b) => (a.name > b.name ? 1 : -1));

    const grouped: CustomHolesByType = { face: [], back: [], edge: [] };
    this.commonHoleList.forEach(h => {
      switch (h.type) {
        case PlateTypeEnum.Face:
          grouped.face.push(h);
          break;

        case PlateTypeEnum.Back:
          grouped.back.push(h);
          break;

        case PlateTypeEnum.Edge:
          grouped.edge.push(h);
          break;
      }
    });

    return grouped;
  }

  constructor(public dialog: MatDialog, private cuttingPanelListService: CuttingPanelListService) {}

  openAddHoleDialog(): void {
    const dialog = forkJoin([
      this.cuttingPanelListService.getCustomHoleSchemas(),
      this.cuttingPanelListService.getFastenerSets(),
    ]).pipe(
      concatMap(res => {
        this.customHoleWizardDialogRef = this.dialog.open(FastenersWizardDialogComponent, {
          disableClose: false,
          panelClass: 'custom-hole-wizard-dialog',
          minWidth: '1000px',
          width: '75vw',
          data: {
            panel: this.panel,
            mainMaterialThickness: this.mainMaterialThickness,
            holeSchemas: res[0],
            holeSetShemas: res[1],
          } as FastenersWizardDialogData,
        });

        return this.customHoleWizardDialogRef.afterClosed();
      })
    );

    dialog.subscribe((result: FastenersWizardDialogResult) => {
      if (result) {
        const fasteners = this.getFasteners();

        const topHoles: CuttingCustomHole[] = [];
        const botHoles: CuttingCustomHole[] = [];
        const leftHoles: CuttingCustomHole[] = [];
        const rightHoles: CuttingCustomHole[] = [];
        const topSets: CuttingFixedFastenersSet[] = [];
        const botSets: CuttingFixedFastenersSet[] = [];
        const leftSets: CuttingFixedFastenersSet[] = [];
        const rightSets: CuttingFixedFastenersSet[] = [];

        const adder = (top: any[], bot: any[], left: any[], right: any[], side: Position, val: any): void => {
          switch (side) {
            case 'tc':
              top.push(val);
              break;
            case 'bc':
              bot.push(val);
              break;
            case 'lc':
              left.push(val);
              break;
            case 'rc':
              right.push(val);
              break;
          }
        };

        (result.holes || []).forEach(h => {
          adder(topHoles, botHoles, leftHoles, rightHoles, h.side, h.item);
        });

        (result.sets || []).forEach(s => {
          adder(topSets, botSets, leftSets, rightSets, s.side, s.item);
        });

        fasteners.top.holes.push(...topHoles);
        fasteners.top.sets.push(...topSets);

        fasteners.bottom.holes.push(...botHoles);
        fasteners.bottom.sets.push(...botSets);

        fasteners.right.holes.push(...rightHoles);
        fasteners.right.sets.push(...rightSets);

        fasteners.left.holes.push(...leftHoles);
        fasteners.left.sets.push(...leftSets);

        this.formChanged.emit(fasteners);
      }
      this.customHoleWizardDialogRef = null;
    });
  }

  deleteFastenerSet(fastenersSet: CuttingFixedFastenersSet, thus?: CustomFastenersComponent): void {
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.topSide.fastenersSets, fastenersSet);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.rightSide.fastenersSets, fastenersSet);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.bottomSide.fastenersSets, fastenersSet);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.leftSide.fastenersSets, fastenersSet);
  }

  deleteCustomHole(hole: CuttingCustomHole, thus?: CustomFastenersComponent): void {
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.topSide.customHoles, hole);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.rightSide.customHoles, hole);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.bottomSide.customHoles, hole);
    (thus || this).deleteFastenerFromSide((thus || this).panel.panelOutline.leftSide.customHoles, hole);
  }

  private deleteFastenerFromSide(
    from: (CuttingFixedFastenersSet | CuttingCustomHole)[],
    fastener: CuttingCustomHole | CuttingFixedFastenersSet
  ): void {
    const index = from.indexOf(fastener);
    if (index !== -1) {
      from.splice(index, 1);
      this.formChanged.emit(this.getFasteners());
      return;
    }
  }

  getFasteners(): FastenersBySides {
    return {
      top: { holes: this.panel.panelOutline.topSide.customHoles, sets: this.panel.panelOutline.topSide.fastenersSets },
      bottom: {
        holes: this.panel.panelOutline.bottomSide.customHoles,
        sets: this.panel.panelOutline.bottomSide.fastenersSets,
      },
      left: {
        holes: this.panel.panelOutline.leftSide.customHoles,
        sets: this.panel.panelOutline.leftSide.fastenersSets,
      },
      right: {
        holes: this.panel.panelOutline.rightSide.customHoles,
        sets: this.panel.panelOutline.rightSide.fastenersSets,
      },
    };
  }

  getBasePointLocalizedName(side: Position, basePoint: FastenerBasePointEnum): Position {
    return FastenerBasePointHelper.getBasePointAnglePosition(side, basePoint);
  }

  toggleSelectedHole(fastener: CuttingCustomHole, thus?: CustomFastenersComponent): void {
    if ((thus || this).isSelectedHole(fastener)) {
      (thus || this).selectedHole = undefined;
      (thus || this).selectedHoleChange.next(undefined);
    } else {
      // unselect selected set
      (thus || this).selectedFastenersSet = undefined;
      (thus || this).selectedFastenersSetChange.next(undefined);

      (thus || this).selectedHole = [fastener];
      (thus || this).selectedHoleChange.next([fastener]);
    }
  }

  toggleSelectedFastenersSet(fastener: CuttingFixedFastenersSet, thus?: CustomFastenersComponent): void {
    if ((thus || this).isSelectedFastenerSets(fastener)) {
      (thus || this).selectedFastenersSet = undefined;
      (thus || this).selectedFastenersSetChange.next(undefined);
    } else {
      // unselect selected set
      (thus || this).selectedHole = undefined;
      (thus || this).selectedHoleChange.next(undefined);

      (thus || this).selectedFastenersSet = fastener;
      (thus || this).selectedFastenersSetChange.next(fastener);
    }
  }

  isSelectedHole(hole: CuttingCustomHole, thus?: CustomFastenersComponent): boolean {
    if ((thus || this).selectedHole && (thus || this).selectedHole.length > 0) {
      return _.isEqual((thus || this).selectedHole[0], hole);
    } else {
      return false;
    }
  }

  isSelectedFastenerSets(item: CuttingFixedFastenersSet, thus?: CustomFastenersComponent): boolean {
    const comparedSet = _.cloneDeep((thus || this).selectedFastenersSet);
    const comparedHSet = _.cloneDeep(item);
    if (comparedSet) {
      comparedSet.holes = [];
    }
    if (comparedHSet) {
      comparedHSet.holes = [];
    }
    return _.isEqual(comparedSet, comparedHSet);
  }
}
